import { BusinessOrder, createBusinessOrder, getEmployee } from '~/services/BusinessTraveller/BusinessTravellerService';
import * as OrdersService from '~/services/OrdersService';
import { formatDateLong, getBrowserTimeZoneAbbreviation, getDayJs } from '~/services/TimeService';

import { REASONS } from './constants';

export const isFreeTextReason = (reason: string, knownReasonValues?: Array<string>) => {
  const reasons = knownReasonValues ?? REASONS.map((reason) => reason.value);
  return !reasons.includes(reason);
};

type LeLiveOfferChangeParams = {
  offer?: App.CustomOffer;
  nextItemType?: App.CustomOfferType;
};

/**
 * Sticky flag for tracking offers that were originally LE LIVE and later changed to another type.
 * Once true, it stays true across future edits.
 */
export const getIsChangedFromLeLiveOffer = ({ offer, nextItemType }: LeLiveOfferChangeParams) => {
  return Boolean(
    offer?.is_changed_from_le_live_offer ||
    (offer?.id && offer?.items?.[0]?.type === 'le_live' && nextItemType != null && nextItemType !== 'le_live'),
  );
};

export const validateVccReservationRequestParams = (
  customOfferStatus: App.CustomOffer['status'],
  vendorCurrencyCode: string,
  vendorCostPrice: number,
  validityTo: string,
  guestName: string,
) => {
  const errors: Array<string> = [];

  // Validate input parameters
  if (customOfferStatus !== 'approved') {
    errors.push('The custom offer status must be approved.');
  }

  // Validate currency code format (3 letters)
  if (!/^[A-Z]{3}$/.test(vendorCurrencyCode)) {
    errors.push('Invalid currency code format - must be 3 uppercase letters.');
  }

  // Validate balance is a positive number
  if (isNaN(vendorCostPrice) || vendorCostPrice <= 0) {
    errors.push('Vendor Cost Price must be a positive number.');
  }

  // Validate dates
  const toDate = new Date(validityTo);

  if (isNaN(toDate.getTime())) {
    errors.push('Invalid dates for VCC card validity.');
  }

  // Validate guest name is not empty
  if (!guestName.trim()) {
    errors.push('Guest name cannot be empty.');
  }

  return errors;
};

type CustomOfferLineItem = App.CustomOfferItem & { transaction_key: string };
type CustomOfferOrderPayloadItem = CustomOfferLineItem | App.ServiceFeeOrderItemPayload;

export async function placeCustomOfferOrder(
  isBusinessTraveller: boolean,
  user: App.User,
  payload: {
    transaction_key: string;
    currency_code: string;
    items: Array<CustomOfferOrderPayloadItem>;
  },
) {
  if (isBusinessTraveller) {
    // Assemble LEBT payload
    const bookingManager = await getEmployee(user.id_member);

    // custom_offer_item and service_fee_item
    const items: BusinessOrder['items'] = payload.items.map((item) => {
      if (item.type === 'service_fee') {
        return {
          transactionKey: item.transaction_key,
          type: 'service_fee',
          travellers: [],
        };
      }
      return {
        transactionKey: item.transaction_key,
        type: 'custom_offer',
        budgetLookup: undefined,
        travellers: item.travellers.map((traveller, travellerIndex) => ({
          employeeId: traveller.employee?.id,
          guestTravellerData: {
            firstName: traveller.first_name || traveller.name || '',
            lastName: traveller.last_name || '',
            email: traveller.email,
          },
          isPrimaryTraveller: travellerIndex === 0,
        })),
      };
    });

    const businessOrderPayload: BusinessOrder = {
      orderPayload: payload,
      transactionKey: payload.transaction_key,
      bookingManagerEmployeeId: bookingManager.id,
      tripRequestId: undefined,
      items,
    };

    // Place LEBT order
    return await createBusinessOrder(bookingManager.businessId, businessOrderPayload);
  } else {
    // Place regular order
    return await OrdersService.createOrder(payload);
  }
}

type FlightTerms = {
  is_changeable: boolean;
  is_cancellable: boolean;
  le_is_changeable: boolean;
  le_is_cancellable: boolean;
  currency: string;
  change_fee?: number;
  le_currency: string;
  le_change_fee?: number;
  cancellation_fee?: number;
  le_cancellation_fee?: number;
};

type BrandConfig = {
  brandName: string;
  includeChangeOfMind: boolean;
  includePoliciesUrl: boolean;
  bookingsPortalName: string;
  /** E-ticket delivery time description, e.g. "10–14 days" or "3 hours" */
  eTicketDeliveryTime: string;
};

/**
 * Internal function to generate flight terms and conditions with brand-specific customization
 */
const generateFlightTermsInternal = (flightTerms: FlightTerms, dueDate: string | null, config: BrandConfig): string => {
  const isAirlineNonChangeable = flightTerms.is_changeable;
  const isAirlineNonRefundable = flightTerms.is_cancellable;
  const isLeNonChangeable = flightTerms.le_is_changeable;
  const isLeNonRefundable = flightTerms.le_is_cancellable;

  const airlineDateChangeFee = isAirlineNonChangeable
    ? 'Non-changeable per person plus any fare & tax difference'
    : `${flightTerms.currency} ${flightTerms.change_fee || '0'} per ticket + any fare difference.`;

  const leDateChangeFee = isLeNonChangeable
    ? 'Non-changeable per person'
    : `${flightTerms.le_currency} ${flightTerms.le_change_fee || '0'} per ticket`;

  const airlineCancellationFee = isAirlineNonRefundable
    ? 'Non-refundable per person plus any fare & tax difference'
    : `${flightTerms.currency} ${flightTerms.cancellation_fee || '0'} per ticket.`;

  const leCancellationFee = isLeNonRefundable
    ? 'Non-refundable per person'
    : `${flightTerms.le_currency} ${flightTerms.le_cancellation_fee || '0'} per ticket.`;

  const paymentDueDate = dueDate
    ? `${formatDateLong(dueDate)} at ${getDayJs(dueDate).format('h:mm A')} ${getBrowserTimeZoneAbbreviation()}`
    : '[DATETIME]';

  const changeOfMindSection = config.includeChangeOfMind
    ? `\n\nChange of Mind: The 7-Day change of mind guarantee does not apply to flights. Airline and ${config.brandName} refund/change policies apply.`
    : '';

  const policiesUrlLine = config.includePoliciesUrl
    ? '\n- For full flight terms and conditions, please refer to: https://luxuryescapes.com/au/flights-policies.'
    : '';

  const terms = `Due Date: Please pay this invoice before ${paymentDueDate}

Itinerary Check: Ensure names match passports and flight dates/times align with your hotel, tour or cruise before paying.${changeOfMindSection}

Booking Names, Passports & Visas:
- Names must match passports exactly. If name corrections are permitted, ${config.brandName} and airline fees may apply.
- Passport must be valid for 6+ months from return date.
- You are responsible for obtaining any required visas.
- For information on visa requirements, visit https://visasdirect.com.au/?login=luxescapes - a third-party provider not affiliated with ${
    config.brandName
  }.

Date Changes${
    !isAirlineNonChangeable ? '\n- Allowed up to 24 hours before departure (subject to seat availability).' : ''
  }
- Airline Fee: ${airlineDateChangeFee}
- ${config.brandName} Fee: ${leDateChangeFee}
- No-show penalties apply.

Cancellations/Refunds${!isAirlineNonRefundable ? '\n- Allowed up to 24 hours before departure.' : ''}
- Airline Fee: ${airlineCancellationFee}
- ${config.brandName} Fee: ${leCancellationFee}
- After departure: Non-refundable.
- No-show penalties apply.

Advance Seats Selection: Seat selection is governed by airline-specific policies and may attract additional charges, which vary depending on the airline, route and fare type; these charges are not automatically included in your purchase or Booking Fee. Where applicable, advance seat selection can generally be arranged directly with the airline via its 'Manage My Booking' portal after ticket issuance.

Important
- Review all your documents carefully. Contact us before payment if any details are incorrect.${policiesUrlLine}
- Pay your invoice by the due date specified on your invoice.
- E-tickets will be sent to your email within ${config.eTicketDeliveryTime} of payment.
- Any flight changes or cancellations after purchase won't appear in ${
    config.bookingsPortalName
  }. To stay up to date, use your flight booking reference to check and manage your booking directly on the airline's website.`;

  return terms;
};

export const generateFlightTermsAndConditions = (flightTerms: FlightTerms | null, dueDate: string | null): string => {
  if (!flightTerms) {
    return '';
  }

  return generateFlightTermsInternal(flightTerms, dueDate, {
    brandName: 'Luxury Escapes',
    includeChangeOfMind: true,
    includePoliciesUrl: true,
    bookingsPortalName: 'MyEscapes',
    eTicketDeliveryTime: '10–14 days',
  });
};

export const generateLEBTFlightTermsAndConditions = (
  flightTerms: FlightTerms | null,
  dueDate: string | null,
): string => {
  if (!flightTerms) {
    return '';
  }

  return generateFlightTermsInternal(flightTerms, dueDate, {
    brandName: 'LE Business Traveller',
    includeChangeOfMind: false,
    includePoliciesUrl: false,
    bookingsPortalName: 'My Bookings',
    eTicketDeliveryTime: '3 hours',
  });
};
